import {
  Grid,
  Container,
  Box,
  Typography
} from '@mui/material';
import MissingContent from '../../../../components/shared/errorMessages/MissingContent';
import { GalleryMediaItem } from '../../types/CMSTypes';

interface CMSConsumerGalleryProps {
  galleryMedia: GalleryMediaItem[] | null;
  title?: string;
  secondaryText?: string;
  hasTitle?: boolean;
  hasSecondaryText?: boolean;
  marginTop?: number;
  marginBottom?: number;
  // Layout options
  galleryLayout?: 'masonry' | 'standard';
  columns?: number;
}

const CMSConsumerGallery: React.FC<CMSConsumerGalleryProps> = ({
                                                                 galleryMedia,
                                                                 title = '',
                                                                 secondaryText = '',
                                                                 hasTitle = false,
                                                                 hasSecondaryText = false,
                                                                 marginTop = 3,
                                                                 marginBottom = 3,
                                                                 galleryLayout = 'masonry',
                                                                 columns = 2
                                                               }) => {

  // Find image by position
  const findImageByPosition = (position: string) => {
    if (!galleryMedia) return null;
    const image = galleryMedia.find((img: GalleryMediaItem) => img.position === position);
    return image?.file_link;
  };

  // Show missing content if no data
  if (!galleryMedia || galleryMedia.length === 0) {
    return <MissingContent sectionName="Gallery" />;
  }

  return (
    <Container maxWidth="lg">
      {/* Title and Secondary Text */}
      <Grid
        mt={marginTop}
        container
        alignItems="center"
        flexDirection="column"
        sx={{ width: "100%" }}
      >
        {hasTitle && <Typography variant="h4">{title}</Typography>}
        {hasSecondaryText && (
          <Typography variant="h6">{secondaryText}</Typography>
        )}
      </Grid>

      {/* Desktop View - 4 Image Layout */}
      <Container
        maxWidth="lg"
        sx={{
          display: { xs: 'none', lg: 'block' },
          mt: marginTop,
          mb: marginBottom
        }}
      >
        <Grid container spacing={2}>
          {/* Left Column */}
          <Grid item xs={6}>
            {/* Top Left - Large Image */}
            <Box overflow="hidden">
              <img
                style={{
                  width: '100%',
                  height: '80vh',
                  objectFit: 'cover',
                  padding: '1rem',
                }}
                src={findImageByPosition('top left') || '/placeholder-image.jpg'}
                alt="gallery-top-left"
              />
            </Box>

            {/* Bottom Left - Small Image */}
            <Box
              overflow="hidden"
              sx={{
                marginTop: '15px',
              }}
            >
              <img
                style={{
                  width: '100%',
                  height: '40vh',
                  objectFit: 'cover',
                  paddingRight: '1rem',
                  paddingLeft: '1rem',
                }}
                src={findImageByPosition('bottom left') || '/placeholder-image.jpg'}
                alt="gallery-bottom-left"
              />
            </Box>
          </Grid>

          {/* Right Column */}
          <Grid
            item
            xs={6}
            sx={{
              padding: '5px',
            }}
          >
            {/* Top Right - Small Image */}
            <Box overflow="hidden" height="400px">
              <img
                style={{
                  width: '100%',
                  height: '40vh',
                  objectFit: 'cover',
                }}
                src={findImageByPosition('top right') || '/placeholder-image.jpg'}
                alt="gallery-top-right"
              />
            </Box>

            {/* Bottom Right - Large Image */}
            <Box overflow="hidden" marginTop="15px">
              <img
                style={{
                  width: '100%',
                  height: '80vh',
                  marginTop: '3px',
                  objectFit: 'cover',
                }}
                src={findImageByPosition('bottom right') || '/placeholder-image.jpg'}
                alt="gallery-bottom-right"
              />
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Mobile View - Single Column */}
      <Grid
        container
        flexDirection="column"
        display={'flex'}
        justifyContent={'center'}
        alignItems="center"
        mt={marginTop}
        mb={marginBottom}
        paddingX="16px"
        sx={{
          display: { xs: 'flex', lg: 'none' }, // Only show on mobile
        }}
      >
        <Box sx={{ maxWidth: '100%' }}>
          {/* Mobile images stacked vertically */}
          {['top left', 'bottom left', 'top right', 'bottom right'].map((position, index) => {
            const imageUrl = findImageByPosition(position);
            if (!imageUrl) return null;

            return (
              <Box key={position} sx={{ mb: 2 }}>
                <img
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'cover',
                  }}
                  src={imageUrl}
                  alt={`gallery-${position.replace(' ', '-')}`}
                />
              </Box>
            );
          })}
        </Box>
      </Grid>
    </Container>
  );
};

export default CMSConsumerGallery;